import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useRillContext } from "../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../utils/get-avatar-url";

/**
 * One request per page, shared by every mounted picker and re-validated after
 * COACHES_TTL_MS. The tour editor renders one picker per time range, which
 * otherwise means an identical fetch per range (and another on every day toggle).
 */
const COACHES_TTL_MS = 60 * 1000;
let coachesCache = { at: 0, promise: null };

function loadCoaches() {
  const now = Date.now();
  if (!coachesCache.promise || now - coachesCache.at > COACHES_TTL_MS) {
    coachesCache = {
      at: now,
      promise: fetch("/actions/scheduling/coaches", {
        credentials: "same-origin",
      })
        .then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          return Array.isArray(data?.coaches) ? data.coaches : [];
        })
        .catch((e) => {
          // A failure must not be cached, or every later mount reuses it.
          coachesCache = { at: 0, promise: null };
          throw e;
        }),
    };
  }
  return coachesCache.promise;
}

const PANEL_GAP = 4;
const PANEL_VIEWPORT_PAD = 8;
const PANEL_CHROME_HEIGHT = 52;
const LIST_MAX_HEIGHT = 240;
const LIST_MIN_HEIGHT = 120;

function getScrollParent(el) {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * Multi-select picker backed by /actions/scheduling/coaches.
 * Props:
 *   id              — element id for the trigger button (used by <label htmlFor>)
 *   value           — string[] of selected coach _ids
 *   onChange        — (ids: string[]) => void
 *   placeholder     — string shown when nothing is selected (default "Select coaches")
 *   disabled        — boolean
 */
const CoachMultiSelect = ({
  id,
  value,
  onChange,
  placeholder = "Select coaches",
  disabled = false,
  single = false,
  emptyLabel = "Unassigned",
}) => {
  const { locals } = useRillContext();
  const theme = locals?.theme;
  const selected = Array.isArray(value) ? value.map(String) : [];
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [panelPos, setPanelPos] = useState(null);
  const wrapRef = useRef(null);
  const triggerWrapRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const singleRef = useRef(single);
  singleRef.current = single;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadCoaches()
      .then((list) => {
        if (cancelled) return;
        setCoaches(list);
        if (!singleRef.current && list.length === 1) {
          onChangeRef.current?.([String(list[0]._id)]);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || "Failed to load coaches");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const updatePanelPosition = useCallback(() => {
    const anchor = triggerWrapRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    let boundTop = PANEL_VIEWPORT_PAD;
    let boundBottom = window.innerHeight - PANEL_VIEWPORT_PAD;
    const scrollParent = getScrollParent(anchor);
    if (scrollParent) {
      const parentRect = scrollParent.getBoundingClientRect();
      const top = parentRect.top + scrollParent.clientTop;
      boundTop = Math.max(boundTop, top);
      boundBottom = Math.min(boundBottom, top + scrollParent.clientHeight);
    }
    const spaceBelow = boundBottom - rect.bottom - PANEL_GAP;
    const spaceAbove = rect.top - boundTop - PANEL_GAP;
    const openUp =
      spaceBelow < PANEL_CHROME_HEIGHT + LIST_MIN_HEIGHT &&
      spaceAbove > spaceBelow;
    const listMaxHeight = Math.max(
      0,
      Math.min(
        LIST_MAX_HEIGHT,
        (openUp ? spaceAbove : spaceBelow) - PANEL_CHROME_HEIGHT
      )
    );
    setPanelPos({ openUp, listMaxHeight });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPanelPos(null);
      return;
    }
    updatePanelPosition();
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", updatePanelPosition);
    return () => window.removeEventListener("resize", updatePanelPosition);
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) return;
    const handleDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleDocMouseDown);
    return () => document.removeEventListener("mousedown", handleDocMouseDown);
  }, [open]);

  const toggleId = (cid) => {
    if (single) {
      onChange?.(selected.includes(cid) ? [] : [cid]);
      setOpen(false);
      return;
    }
    const set = new Set(selected);
    if (set.has(cid)) set.delete(cid);
    else set.add(cid);
    onChange?.(Array.from(set));
  };

  const chooseNone = () => {
    onChange?.([]);
    setOpen(false);
  };

  const clearAll = (e) => {
    e?.stopPropagation?.();
    onChange?.([]);
  };

  const filtered = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return coaches;
    return coaches.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)
    );
  })();

  const selectedCoaches = coaches.filter((c) => selected.includes(c._id));
  const triggerLabel =
    selectedCoaches.length === 0
      ? single
        ? emptyLabel
        : placeholder
      : selectedCoaches.length === 1
        ? selectedCoaches[0].name
        : `${selectedCoaches.length} coaches selected`;

  const avatarFor = (coach) =>
    coach?.profile_image_url || getAvatarUrl(coach || {}, false, theme);

  if (!single && !loading && !error && coaches.length === 1) {
    const sole = coaches[0];
    return (
      <div className="coach-multi-select coach-multi-select--readonly">
        <div className="coach-multi-select-trigger-wrap">
          <div className="coach-multi-select-trigger">
            <img
              src={avatarFor(sole)}
              alt=""
              className="coach-multi-select-avatar"
            />
            <span className="coach-multi-select-trigger-label">
              {sole.name}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coach-multi-select" ref={wrapRef}>
      <div className="coach-multi-select-trigger-wrap" ref={triggerWrapRef}>
        <button
          type="button"
          id={id}
          className="coach-multi-select-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
        >
          {selectedCoaches.length === 1 && (
            <img
              src={avatarFor(selectedCoaches[0])}
              alt=""
              className="coach-multi-select-avatar"
            />
          )}
          {selectedCoaches.length > 1 && (
            <span className="coach-multi-select-avatar-stack" aria-hidden>
              {selectedCoaches.slice(0, 3).map((c) => (
                <img
                  key={c._id}
                  src={avatarFor(c)}
                  alt=""
                  className="coach-multi-select-avatar coach-multi-select-avatar--stacked"
                />
              ))}
            </span>
          )}
          <span
            className={
              selectedCoaches.length === 0
                ? "coach-multi-select-trigger-label is-placeholder"
                : "coach-multi-select-trigger-label"
            }
          >
            {triggerLabel}
          </span>
          <img
            src="/img/new-icons/chevron-down.svg"
            alt=""
            className="coach-multi-select-chevron"
            aria-hidden
          />
        </button>
        {selectedCoaches.length > 0 && (
          <button
            type="button"
            className="coach-multi-select-clear"
            aria-label="Clear selection"
            onClick={clearAll}
          >
            ×
          </button>
        )}
      </div>

      {open && panelPos && (
        <div
          className={
            panelPos.openUp
              ? "coach-multi-select-panel coach-multi-select-panel--up"
              : "coach-multi-select-panel"
          }
          role="listbox"
        >
          <div className="coach-multi-select-search">
            <input
              type="text"
              placeholder="Search coaches…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search coaches"
            />
          </div>
          {loading ? (
            <div className="coach-multi-select-empty">Loading coaches…</div>
          ) : error ? (
            <div className="coach-multi-select-empty" role="alert">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="coach-multi-select-empty">
              {coaches.length === 0
                ? "No coaches found."
                : "No coaches match your search."}
            </div>
          ) : (
            <ul
              className="coach-multi-select-list"
              style={{ maxHeight: panelPos.listMaxHeight }}
            >
              {single && (
                <li>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected.length === 0}
                    className={
                      selected.length === 0
                        ? "coach-multi-select-option is-selected coach-multi-select-option--none"
                        : "coach-multi-select-option coach-multi-select-option--none"
                    }
                    onClick={chooseNone}
                  >
                    <span
                      aria-hidden
                      className={
                        selected.length === 0
                          ? "coach-multi-select-check is-checked"
                          : "coach-multi-select-check"
                      }
                    >
                      <img
                        src={
                          selected.length === 0
                            ? "/img/new-icons/checked-square.svg"
                            : "/img/new-icons/unchecked-square.svg"
                        }
                        alt=""
                      />
                    </span>
                    <span className="coach-multi-select-option-body">
                      <span className="coach-multi-select-option-name">
                        {emptyLabel}
                      </span>
                      <span className="coach-multi-select-option-email">
                        Gym is notified to assign someone
                      </span>
                    </span>
                  </button>
                </li>
              )}
              {filtered.map((c) => {
                const isSelected = selected.includes(c._id);
                return (
                  <li key={c._id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={
                        isSelected
                          ? "coach-multi-select-option is-selected"
                          : "coach-multi-select-option"
                      }
                      onClick={() => toggleId(c._id)}
                    >
                      <span
                        aria-hidden
                        className={
                          isSelected
                            ? "coach-multi-select-check is-checked"
                            : "coach-multi-select-check"
                        }
                      >
                        {isSelected && (
                          <img src="/img/new-icons/checked-square.svg" alt="" />
                        )}
                        {!isSelected && (
                          <img
                            src="/img/new-icons/unchecked-square.svg"
                            alt=""
                          />
                        )}
                      </span>
                      <img
                        src={avatarFor(c)}
                        alt=""
                        className="coach-multi-select-avatar"
                      />
                      <span className="coach-multi-select-option-body">
                        <span className="coach-multi-select-option-name">
                          {c.name}
                        </span>
                        {c.email && (
                          <span className="coach-multi-select-option-email">
                            {c.email}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default CoachMultiSelect;
